import React, { useMemo } from 'react';
import block from 'bem-cn';
import { useSelector, shallowEqual } from 'react-redux';
import dayjs from 'dayjs';

// import Paginator from 'components/Paginator/PaginatorSwitcher/desktop';
import Spinner from 'components/Spinner/desktop';
import { LineChart } from 'components/Charts/view/desktop/LineChart';
import Switcher from 'components/Switcher';
import SVG from 'components/SVG';

import useModel, { EStatisticsTab } from 'features/statistic/model/useModel';

// import { useAppSelector } from 'shared/hooks';

import usersSVG from './img/users.svg';
import totalBetsSVG from './img/totalBets.svg';
import totalNetwinSVG from './img/totalNetwin.svg';
import totalWinSVG from './img/totalWin.svg';
import activeUserSVG from './img/active-user.svg';
import marginSVG from './img/margin.svg';
import infoSVG from './img/info.svg';

import Filters from './Filters';
import Table from './Table';
import TableBalance from './TableBalance';

import './Statistic.scss';
import { formateNumber } from 'shared/helpers';

const b = block('statistic-desktop');

const Statistic = () => {
  const model = useModel();

  const { actionProcessing, statistic, balanceStatistic } = useSelector(state => state.statistic, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);

  const switchTab = tabName => {
    model.inputState.setActiveTab(tabName);
  };

  const formatDifferenceItem = (percent) => {
    const flag = Number(percent) === 0 ? '' : Number(percent) > 0 ? '↑' : '↓';
    return (
      <div className={b('top-right-item__1', { raise: Number(percent) > 0, empty: flag === '' })}>
        <span>{flag}</span> {`${percent} %`}
      </div>
    )
  };

  const svgMap = {
    users: usersSVG,
    totalBets: totalBetsSVG,
    totalNetwin: totalNetwinSVG,
    totalWin: totalWinSVG,
    activeUser: activeUserSVG,
    margin: marginSVG,
  };

  const topItems = model.inputState.buildTopItems(locale);

  const {
    inputState: { tabs, setActiveTab, isAsc, setIsAsc },
  } = model;

  const activeTab = useMemo(() => model.inputState.activeTab, [model.inputState.activeTab]);

  const tableComponent = useMemo(() => {
    switch (activeTab) {
      case EStatisticsTab.agentsStatistics:
        return (
          <div className={b('content')}>
            <Table data={statistic.rankedAgents} type={activeTab} />
          </div>
        );
      case EStatisticsTab.playersStatistics:
        return (
          <div className={b('content')}>
            <Table data={statistic.rankedPlayers} type={activeTab} />
          </div>
        );
      case EStatisticsTab.agentsBalance:
        return (
          <div className={b('content')}>
            <TableBalance data={balanceStatistic} setIsAsc={setIsAsc} isAsc={isAsc} />
          </div>
        );
      case EStatisticsTab.playersBalance:
        return (
          <div className={b('content')}>
            <TableBalance data={balanceStatistic} setIsAsc={setIsAsc} isAsc={isAsc} />
          </div>
        );
    }
  }, [activeTab, balanceStatistic, statistic, isAsc, setIsAsc]);

  return (
    <div className={b()}>
      <Spinner isLoading={model.outputState.actionProcessing} />
      <div className={b('title')}>{locale.myStatistic}</div>
      <div className={b('top')}>
        <div className={b('top-left')}>
          {topItems.map(item => (
            <div className={b('top-left-item')} key={item.key}>
              <div className={b('left')}>
                <SVG className={b('top-left-item-icon')} svgProps={{ svg: svgMap[item.icon] }} />
                <div className={b('top-left-item-1')}>
                  <div className={b('top-left-item-11')}>{item.left.value}</div>
                  <div className={b('top-left-item-12')}>{item.left.label}</div>
                </div>
              </div>
              <div className={b('right', { type: item.key })}>
                {item.info && (
                  <div className={b('info')}>
                    <span className={b('info-text')}>info</span>
                    <SVG className={b('info-icon')} svgProps={{ svg: infoSVG }} />
                    <div className={b('info-description')}>{item.info.description}</div>
                  </div>
                )}
                {item.isComparative && (
                  <div className={b('compare')}>
                    {locale.compare}
                    <Switcher
                      key={item.key}
                      isActive={model.outputState.comparisonType === item.compareKey}
                      onSwitch={() => model.inputState.onChangeComparisonType(item.compareKey)}
                    />
                  </div>
                )}
                {!model.outputState.actionProcessing && item.showRight && item.isComparative && (
                  <div className={b('top-right-item')}>
                    {formatDifferenceItem(item.diffPercent)}
                    <div className={b('top-right-item__2')}>{formateNumber(item.diff)}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className={b('top-right')}>
          <div className={b('filters')}>
            <Filters model={model} />
          </div>

          <div className={b('table')}>
            <LineChart
              title={model.outputState.data.title}
              labels={model.outputState.data?.time || []}
              originalLabels={model.outputState.data?.originalTime || []}
              colorFill={model.outputState.data?.colorFill}
              color={model.outputState.data?.color}
              data={model.outputState.data?.data || []}
              secondData={model.outputState.data2?.data || []}
              secondColor={model.outputState.data2?.color}
              secondColorFill={model.outputState.data2?.colorFill}
              originalLabels2={model.outputState.data2?.originalTime || []}
            />
          </div>
        </div>
      </div>
      <div>
        <div className={b('center')}>
          <div className={b('tabs')}>
            {tabs.map(tab => {
              const onChange = () => {
                setActiveTab(tab.value);
              };

              return (
                <div onClick={onChange} className={b('tab', { active: tab.active })}>
                  <span className={b('tab-title')}>{tab.title}</span>
                  {tab?.amount !== undefined && <span className={b('tab-amount')}>{tab.amount}</span>}
                </div>
              );
            })}
          </div>
        </div>
        <div className={b('bottom')}>{tableComponent}</div>
      </div>
    </div>
  );
};

export default Statistic;
